import React from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import SelectBox from '../../shared/component/SelectBox';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { MemberInfoValue, MemberTitleInput } from '../../shared/component/headless/input/InputBody';
import { getMemberNamePlaceholder, getPageMessage } from '../../shared/constants/pageMessages';
import { getHintMessage } from '../../shared/constants/hintMessages';
import {
  AlertPosition,
  AlertType,
  CountryType,
  DeviceType,
  GroupMemberRole,
  GroupMemberStatus,
  ModalType,
  SelectType,
  TextAlignment,
} from '../../shared/enum/EnumRepository';
import { useGroupMemberStore } from '../../shared/store/groupMemberStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useValidation } from '../../shared/hooks/useValidation';
import { GroupMemberData } from '../../shared/dto/EntityRepository';
import { useGroupMember } from '../../shared/hooks/useApi/useGroupMember';
import toggleIcon from '../../asset/icon/toggle.png';
import HintSmallWrapper from '../../shared/component/headless/button/HintSmallWrapper';
import { useGroupMemberTagStore } from '../../shared/store/groupMemberTagStore';
import GroupMemberLabelInViewer from './GroupMemberLabelInViewer';
import linkIcon from '../../asset/icon/link.png';
import linkCutIcon from '../../asset/icon/linkCut.png';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowGroupMemberTagList } from '../../shared/util/visibility/groupMemberVisibility';
import { useAuthStore } from '../../shared/store/authStore';
import {
  selectPendingGroupMemberConnectionRequest,
  useApprovalRequestStore,
} from '../../shared/store/approvalRequestStore';
import { useApprovalRequestActions } from '../../shared/hooks/useApi/useApprovalRequest';
import { canShowOwnedResource } from '../../shared/util/scopedResource';
import { useTempAlertStore } from '../../shared/store/portal/tempAlertStore';

/** Country가 NONE이면 (b. year)만, year가 비면 (country)만, 둘 다 있으면 (b. year, country). 빈 문자열·"0" 등은 Number가 0이라 (b. 미표시) */
function formatInlineMemberInfo(year: number, country: CountryType): string {
  const hasYear = Number(year) !== 0;
  if (country === CountryType.NONE) {
    return hasYear ? `(b. ${year})` : '';
  }
  if (!hasYear) {
    return `(${country})`;
  }
  return `(b. ${year}, ${country})`;
}

interface GroupMemberProfileAndInfoProps {
  member: GroupMemberData;
}

const GroupMemberProfileAndInfo: React.FC<GroupMemberProfileAndInfoProps> = ({ member }) => {
  const { isEditMode } = useEditMode();
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const updateGroupMemberLocal = useGroupMemberStore((s) => s.updateGroupMemberLocal);
  const tags = useGroupMemberTagStore((s) => s.tags);
  const listOwnerAui = useGroupMemberTagStore((s) => s.listOwnerAui);
  const tagStatus = useGroupMemberTagStore((s) => s.status);
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const accountAui = useAuthStore((s) => s.user?.aui);
  const requestListOwnerAui = useApprovalRequestStore((s) => s.listOwnerAui);
  const requestListStatus = useApprovalRequestStore((s) => s.status);
  const pendingConnectionRequest = useApprovalRequestStore(
    selectPendingGroupMemberConnectionRequest(accountAui, member.id),
  );
  const invitationStateReady = canShowOwnedResource({
    pageOwnerAui: accountAui,
    resourceOwnerAui: requestListOwnerAui,
    status: requestListStatus,
  });
  const canShowTags = canShowGroupMemberTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status: tagStatus,
  });
  const labelOptions = canShowTags ? tags : [];
  const { updateGroupMemberStatus, unlinkGroupMember } = useGroupMember();
  const { cancelApprovalRequest } = useApprovalRequestActions();
  const { setStandardModal } = useModalStore();
  const { enqueueAlert } = useStandardAlertStore();
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const { checkType } = useValidation();

  const handleChangeWithValidate = (
    updates: Partial<Pick<GroupMemberData, 'name' | 'nameEn' | 'country'>>,
  ) => {
    for (const key in updates) {
      const field = key as 'name' | 'nameEn' | 'country';
      const value = updates[field];
      if (typeof value === 'string') {
        if (!checkType(field, value)) {
          return;
        }
      }
    }
    updateGroupMemberLocal(member.id, updates);
  };

  const handleYearChange = (value: string) => {
    if (!checkType('year', value)) return;
    updateGroupMemberLocal(member.id, { year: value === '' ? 0 : Number(value) });
  };

  const handleStatusToggle = async () => {
    await updateGroupMemberStatus({
      id: member.id,
      status: member.status === GroupMemberStatus.ACTIVE
        ? GroupMemberStatus.INACTIVE
        : GroupMemberStatus.ACTIVE,
    });
  };

  const handleOpenInviteModal = () => {
    setStandardModal({
      modalType: ModalType.GROUP_MEMBER_INVITE,
      title: null,
      value: member.id,
      handleChange: () => {},
    });
  };

  const handleUnlink = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_UNLINK_GROUP_MEMBER'),
      callBack: async () => {
        await unlinkGroupMember(member.id);
      },
    });
  };

  const handleCancelInvitation = () => {
    if (!pendingConnectionRequest) return;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_CANCEL_GROUP_MEMBER_INVITATION'),
      callBack: async () => {
        const canceled = await cancelApprovalRequest({ id: pendingConnectionRequest.id });
        if (canceled) setUpdatedTempAlert();
      },
    });
  };

  const { name, year, country, role } = member;
  const nameFieldValue = name;
  const showNameDefaultLabel = !String(nameFieldValue ?? '').trim();
  const nameDefaultLabel = getPageMessage('MEMBER_NAME_DEFAULT');
  const inlineMemberInfo = formatInlineMemberInfo(year, country);
  const isLinked = !!(member.linkedMemberAui || member.linkedMemberHandle);
  return (
    <ProfileAndInfo>
      {isEditMode ? (
        <>
          <NameFieldRow $isMobile={isMobile}>
            <MemberNameInputWrap>
              <HeadlessInput
                type="text"
                value={nameFieldValue}
                placeholder={getMemberNamePlaceholder()}
                handleChange={(e) => handleChangeWithValidate({ name: e.target.value })}
                StyledInput={MemberTitleInput}
                $alignment={TextAlignment.LEFT}
              />
            </MemberNameInputWrap>
            <StatusToggleRow>
              <HintSmallWrapper
                content={
                  member.status === GroupMemberStatus.ACTIVE
                    ? getHintMessage('MEMBER_STATUS_INACTIVE')
                    : getHintMessage('MEMBER_STATUS_ACTIVE')
                }
                toBottom={true}
              >
                <UniversalIconBtn
                  size="small"
                  border={false}
                  isTransparent={true}
                  backgroundImg={toggleIcon}
                  onClick={handleStatusToggle}
                  aria-label="Toggle member status"
                />
              </HintSmallWrapper>
              <HintSmallWrapper
                content={getHintMessage(
                  isLinked
                    ? 'DISCONNECT_ACCOUNT'
                    : pendingConnectionRequest
                      ? 'CANCEL_INVITATION'
                      : 'INVITE_ACCOUNT',
                )}
                toBottom={true}
              >
                {isLinked ? (
                  <UniversalIconBtn
                    size="small"
                    border={false}
                    isTransparent={true}
                    backgroundImg={linkCutIcon}
                    onClick={handleUnlink}
                    aria-label={getHintMessage('DISCONNECT_ACCOUNT')}
                  />
                ) : pendingConnectionRequest ? (
                  <UniversalIconBtn
                    size="small"
                    border={false}
                    isTransparent={true}
                    backgroundImg={linkCutIcon}
                    onClick={handleCancelInvitation}
                    aria-label={getHintMessage('CANCEL_INVITATION')}
                  >
                    {getPageMessage('GROUP_MEMBER_INVITATION_PENDING')}
                  </UniversalIconBtn>
                ) : (
                  <UniversalIconBtn
                    size="small"
                    border={false}
                    isTransparent={true}
                    backgroundImg={linkIcon}
                    onClick={handleOpenInviteModal}
                    disabled={!invitationStateReady}
                    aria-label={getHintMessage('INVITE_ACCOUNT')}
                  />
                )}
              </HintSmallWrapper>
            </StatusToggleRow>
          </NameFieldRow>
          <BornFieldRow>
            <BornFieldLabel>Born</BornFieldLabel>
            <MoleculeInputDiv
              value={year}
              defaultValue={'0000'}
              DefaultDiv={BornFieldDefault}
              placeholder={'0000'}
              handleChange={(e) => handleYearChange(e.target.value)}
              inputStyle={MemberInfoValue}
              StyledDiv={BornFieldValue}
            />
          </BornFieldRow>
          <BornFieldRow>
            <BornFieldLabel>Country</BornFieldLabel>
            <CountrySelectBoxWrapper>
              <SelectBox
                value={country}
                selectType={SelectType.COUNTRY}
                handleChange={(value: CountryType) => handleChangeWithValidate({ country: value })}
                direction={false}
              />
            </CountrySelectBoxWrapper>
          </BornFieldRow>
          <BornFieldRow>
            <BornFieldLabel>Role</BornFieldLabel>
            <CountrySelectBoxWrapper>
              <SelectBox
                value={role}
                selectType={SelectType.GROUP_MEMBER_ROLE}
                handleChange={(value: GroupMemberRole) =>
                  updateGroupMemberLocal(member.id, { role: value })
                }
                direction={false}
              />
            </CountrySelectBoxWrapper>
          </BornFieldRow>
          <LabelFieldRow>
            <BornFieldLabel>Label</BornFieldLabel>
            <LabelContainer>
              {labelOptions.map((label) => (
                <GroupMemberLabelInViewer
                  key={label.id}
                  memberId={member.id}
                  memberLabels={member.tags}
                  label={label}
                />
              ))}
            </LabelContainer>
          </LabelFieldRow>
        </>
      ) : (
        <NameTitleValueSection>
          {showNameDefaultLabel ? (
            <NameTitleDefaultDiv>{nameDefaultLabel}</NameTitleDefaultDiv>
          ) : (
            <NameTitleValue>
              {nameFieldValue}
                      {isLinked && (
            <LinkedAccountIcon
              src={linkIcon}
              alt={getPageMessage('GROUP_MEMBER_CONNECTED_ACCOUNT')}
              title={getPageMessage('GROUP_MEMBER_CONNECTION_HINT')}
            />
          )}
            </NameTitleValue>
          )}

          <InlineMemberInfo>{inlineMemberInfo}</InlineMemberInfo>
        </NameTitleValueSection>
      )}
    </ProfileAndInfo>
  );
};

const ProfileAndInfo = styled.div`
  display: flex;
  gap: clamp(8px, 2vw, 10px);
  width: fit-content;
  height: fit-content;
  flex-direction: column;
  margin-bottom: clamp(8px, 2vw, 16px);
`;

const NameTitleValue = styled.h1`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.typography.Body_01_1};
`;

const InlineMemberInfo = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const LinkedAccountIcon = styled.img`
  width: 10px;
  height: auto;
  object-fit: contain;
  flex-shrink: 0;
  margin-left: 4px;
  padding-bottom: 1px;
`;

const NameFieldRow = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: ${({ $isMobile }) => ($isMobile ? '8px' : '10px')};
`;

const MemberNameInputWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatusToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  ${({ theme }) => theme.typography.Body_04};
`;

const NameTitleValueSection = styled.div`
  width: 100%;
  padding: 5px 0px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_01_1};
`;

const NameTitleDefaultDiv = styled.div`
  width: fit-content;
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_05};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const BornFieldRow = styled.div`
  display: flex;
  height: 20px;
`;

const LabelFieldRow = styled.div`
  display: flex;
  min-height: 20px;
`;

const LabelContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  ${({ theme }) => theme.typography.Body_04};
`;

const BornFieldLabel = styled.div`
  width: clamp(100px, 10vw, 160px);
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const BornFieldValue = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const BornFieldDefault = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_03_1};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const CountrySelectBoxWrapper = styled.article`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_1};
`;

export default GroupMemberProfileAndInfo;
